import { IFolder } from "api/rpc/2024-04/facilityAdmin/product/folder";

export const PRODUCT_FOLDERS_KEY = "product_folders";

const DB_NAME = "tee-on-admin";
const DB_VERSION = 1;
const STORE_NAME = "product_folders";

let dbPromise: Promise<IDBDatabase> | null = null;
let migratePromise: Promise<void> | null = null;

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function waitForTransaction(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

function openDatabase(): Promise<IDBDatabase> {
  if (dbPromise !== null) {
    return dbPromise;
  }

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not available"));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      db.onclose = () => {
        dbPromise = null;
      };
      resolve(db);
    };

    request.onerror = () => {
      dbPromise = null;
      reject(request.error);
    };
  });

  return dbPromise;
}

function readLegacyLocalStorageFolders(): IFolder[] {
  try {
    const raw = localStorage.getItem(PRODUCT_FOLDERS_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as IFolder[] | null;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

async function migrateLocalStorageFolders(db: IDBDatabase): Promise<void> {
  if (migratePromise === null) {
    migratePromise = (async () => {
      const legacyFolders = readLegacyLocalStorageFolders();
      if (legacyFolders.length === 0) {
        localStorage.removeItem(PRODUCT_FOLDERS_KEY);
        return;
      }

      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);

      legacyFolders
        .filter(folder => folder?.id != null)
        .forEach(folder => {
          store.put(folder);
        });

      await waitForTransaction(transaction);
      localStorage.removeItem(PRODUCT_FOLDERS_KEY);
    })().catch(error => {
      migratePromise = null;
      throw error;
    });
  }

  return migratePromise;
}

async function withStore<T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDatabase();
  await migrateLocalStorageFolders(db);

  const transaction = db.transaction(STORE_NAME, mode);
  return requestToPromise(operation(transaction.objectStore(STORE_NAME)));
}

export function isStoredProductFolderFresh<T extends { updated_at?: string }>(
  selectedFolder: T,
  storedFolder?: T,
): storedFolder is T {
  if (!storedFolder) {
    return false;
  }

  const selectedFolderDate = new Date(selectedFolder.updated_at);
  const storedProductFolderDate = new Date(storedFolder.updated_at);

  return (
    !isNaN(selectedFolderDate.getTime()) &&
    !isNaN(storedProductFolderDate.getTime()) &&
    storedProductFolderDate.getTime() >= selectedFolderDate.getTime()
  );
}

export async function getStoredProductFolder<T = IFolder>(folderId?: number): Promise<T | undefined> {
  if (folderId == null) {
    return undefined;
  }

  try {
    const folder = await withStore("readonly", store => store.get(folderId));
    return folder as T | undefined;
  } catch (error) {
    console.error("Failed to load product folder from IndexedDB", error);
    return undefined;
  }
}

export async function upsertStoredProductFolder<T extends { id: number }>(folder: T): Promise<T> {
  if (folder?.id == null) {
    return folder;
  }

  try {
    await withStore("readwrite", store => store.put(folder));
  } catch (error) {
    console.error("Failed to save product folder to IndexedDB", error);
  }

  return folder;
}

export async function clearStoredProductFolders(): Promise<void> {
  localStorage.removeItem(PRODUCT_FOLDERS_KEY);

  try {
    await withStore("readwrite", store => store.clear());
  } catch (error) {
    console.error("Failed to clear product folders from IndexedDB", error);
  }
}
